import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Link2, Link2Off, Search, Check, Loader2, PackageSearch, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const TABS = [
  { id: "unlinked", label: "未紐づけ" },
  { id: "linked", label: "紐づけ済み" },
  { id: "all", label: "すべて" },
];

export default function AdhocLinksManagement() {
  const [tab, setTab] = useState("unlinked");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [target, setTarget] = useState(null); // group being linked
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null); // {id,name,code,unit}
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const doSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/sync-products`, {}, { ...getAuthHeaders(), timeout: 180000 });
      const d = res.data || {};
      toast.success(`商品マスタを同期しました（新規 ${d.products_added ?? 0} / 更新 ${d.products_updated ?? 0} 件）`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "商品マスタの同期に失敗しました");
    } finally {
      setSyncing(false);
    }
  };

  const fetchGroups = async (t) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/adhoc-links?status=${t}`, getAuthHeaders());
      setGroups(res.data?.groups || []);
    } catch (e) {
      toast.error("一覧の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups(tab);
  }, [tab]);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(
          `${API}/products/paginated?search=${encodeURIComponent(q)}&page_size=25`,
          getAuthHeaders()
        );
        setResults(res.data?.items || []);
      } catch (e) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const openLink = (g) => {
    setTarget(g);
    setSelected(
      g.linked_product_id
        ? { id: g.linked_product_id, name: g.linked_product_name, code: g.linked_product_code }
        : null
    );
    setQuery("");
    setResults([]);
    setDialogOpen(true);
  };

  const doLink = async () => {
    if (!selected) return toast.error("紐づけ先の商品を選んでください");
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/adhoc-links`,
        { product_id: selected.id, other_order_ids: target.other_order_ids },
        getAuthHeaders()
      );
      toast.success(`「${target.product_name}」を「${selected.name}」に紐づけました`);
      setDialogOpen(false);
      fetchGroups(tab);
    } catch (e) {
      toast.error(e.response?.data?.detail || "紐づけに失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const doUnlink = async (g) => {
    if (!window.confirm(`「${g.product_name}」の紐づけを解除しますか？\n（解除後はデスクへリストなし明細として送られます）`)) return;
    try {
      await axios.post(
        `${API}/admin/adhoc-links/unlink`,
        { other_order_ids: g.other_order_ids },
        getAuthHeaders()
      );
      toast.success("紐づけを解除しました");
      fetchGroups(tab);
    } catch (e) {
      toast.error("解除に失敗しました");
    }
  };

  return (
    <div className="space-y-6" data-testid="adhoc-links-management">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-7 bg-emerald-600 rounded-full" />
            <h1 className="text-2xl font-bold text-slate-800">リストなし紐づけ</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            顧客が自由入力した「リストなし発注」を、ポータルの商品マスタに紐づけます。紐づけ済みの明細は、デスクへ<strong>通常明細（商品コード付き）</strong>として送られるため、デスク側での手作業紐づけは不要になります。紐づけ先が無いものはリストなしのままデスクへ送られます。
          </p>
          <p className="text-xs text-amber-700 mt-1">
            ※ 正しく紐づけるには、ポータルの商品マスタが最新である必要があります。右のボタンで最新化できます。
          </p>
        </div>
        <Button
          onClick={doSync}
          disabled={syncing}
          variant="outline"
          className="shrink-0"
          data-testid="adhoc-sync-products-btn"
        >
          {syncing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
          {syncing ? "同期中..." : "商品マスタを同期"}
        </Button>
      </div>

      <div className="flex gap-1 border-b border-slate-200" data-testid="adhoc-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`adhoc-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "border-emerald-600 text-emerald-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm py-10 text-center">読み込み中...</div>
      ) : groups.length === 0 ? (
        <div className="text-slate-400 text-sm py-12 text-center border border-dashed border-slate-200 rounded-lg" data-testid="adhoc-empty">
          {tab === "unlinked"
            ? "未紐づけのリストなし発注はありません。"
            : tab === "linked"
            ? "紐づけ済みのリストなし発注はありません。"
            : "リストなし発注はありません。"}
        </div>
      ) : (
        <div className="grid gap-2.5">
          {groups.map((g) => (
            <div
              key={g.group_key}
              data-testid={`adhoc-group-${g.group_key}`}
              className="bg-white border border-slate-200 rounded-xl p-4 flex gap-4 items-start"
            >
              <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
                <PackageSearch className="h-5 w-5 text-purple-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-800">{g.product_name}</span>
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 font-mono">
                    {g.unit}
                  </span>
                  <span className="text-xs text-slate-400">{g.count}件</span>
                  {g.is_linked ? (
                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <Link2 className="h-3 w-3" />
                      {g.linked_product_name}
                      {g.linked_product_code ? `（${g.linked_product_code}）` : ""}
                    </span>
                  ) : (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                      未紐づけ
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  店舗: {g.store_name}（{g.store_code}） ／ 納品日: {g.delivery_dates.slice(0, 6).join(", ")}
                  {g.delivery_dates.length > 6 ? ` 他${g.delivery_dates.length - 6}件` : ""}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button
                  size="sm"
                  onClick={() => openLink(g)}
                  data-testid={`adhoc-link-btn-${g.group_key}`}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
                >
                  <Link2 className="h-3.5 w-3.5 mr-1" />
                  {g.is_linked ? "変更" : "紐づけ"}
                </Button>
                {g.is_linked && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => doUnlink(g)}
                    data-testid={`adhoc-unlink-btn-${g.group_key}`}
                    className="h-8 text-xs text-rose-500 hover:text-rose-600"
                  >
                    <Link2Off className="h-3.5 w-3.5 mr-1" />解除
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="adhoc-link-dialog">
          <DialogHeader>
            <DialogTitle>マスタ商品に紐づけ</DialogTitle>
            <DialogDescription>
              {target && (
                <>
                  「<strong>{target.product_name}</strong>（{target.unit}・{target.count}件）」を、下から選んだ商品マスタに紐づけます。数量・単位は入力どおり維持されます。
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {selected && (
              <div className="flex items-center justify-between gap-2 text-sm bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="adhoc-selected">
                <span className="inline-flex items-center gap-1.5 text-emerald-800">
                  <Check className="h-4 w-4" />
                  {selected.name}
                  {selected.code ? <span className="font-mono text-xs text-emerald-600">（{selected.code}）</span> : null}
                  {selected.sub_display ? (
                    <span className="px-1.5 py-0 rounded text-[11px] font-bold bg-amber-100 text-amber-700 border border-amber-300">
                      {selected.sub_display}
                    </span>
                  ) : null}
                </span>
                <button type="button" onClick={() => setSelected(null)} className="text-xs text-emerald-700 hover:underline">
                  選び直す
                </button>
              </div>
            )}
            <div className="relative">
              <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="商品名・コード・検索ワードで検索"
                className="pl-8"
                data-testid="adhoc-product-search"
                lang="ja"
              />
            </div>
            {(searching || results.length > 0) && (
              <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-slate-100">
                {searching && <div className="px-3 py-2 text-xs text-slate-400">検索中...</div>}
                {results.map((p) => {
                  const sel = selected?.id === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSelected({ id: p.id, name: p.name, code: p.code, unit: p.unit, sub_display: p.sub_display })}
                      data-testid={`adhoc-product-option-${p.id}`}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-slate-50 ${sel ? "bg-emerald-50" : ""}`}
                    >
                      <span className="truncate flex items-center gap-1">
                        {p.name}
                        {p.code ? <span className="font-mono text-xs text-slate-400">{p.code}</span> : null}
                        {p.sub_display ? (
                          <span className="px-1.5 py-0 rounded text-[11px] font-bold bg-amber-100 text-amber-700 border border-amber-300" data-testid={`adhoc-subdisplay-${p.id}`}>
                            {p.sub_display}
                          </span>
                        ) : null}
                      </span>
                      {sel && <Check className="h-4 w-4 text-emerald-600 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>キャンセル</Button>
            <Button
              onClick={doLink}
              disabled={saving || !selected}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="adhoc-link-save-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Link2 className="h-4 w-4 mr-1" />}
              紐づける
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
